import moment from "moment";
import { useEffect, useState } from "react";
import type { InputOnChangeData, RatingProps } from "semantic-ui-react";
import { Button, Dimmer, Icon, Input, Loader, Message, Rating, Table } from "semantic-ui-react";
import type { FoodOrder, FoodOrderItem } from "types";
import { v4 as uuid } from "uuid";

import { useAuthContext } from "../../../../contexts/AuthContext";
import FoodPoster from "../../../../data/FoodPoster";

/**
 * What the per-order feedback panel accepts. Single call site inside
 * `./table.tsx`'s feedback modal. NOT the same component as
 * `components/common/food/feedback/index.tsx` — they share a name, no code.
 *
 * `order` is optional because `table.tsx` passes its `selectedOrder` state,
 * seeded `{}` and only filled when a row's feedback icon is clicked. The mount
 * effect nevertheless dereferences `props.order` unguarded; the modal is only
 * rendered after `openFeedback` has set a real order.
 */
type FeedbackProps = {
  /**
   * The order being rated. Every path dereferences it unguarded — including
   * writing `itemId` and `name` back onto it for non-catalog orders, which
   * mutates the caller's object.
   */
  order?: FoodOrder & { itemId?: string; name?: string };
  /**
   * Existing feedback, keyed by order id then item id.
   *
   * `liked` and `rating` mirror the local rating maps rather than staying
   * `unknown`: this effect copies them straight across, and `liked` is compared
   * against `null` explicitly (so a cleared rating is distinct from an absent one).
   */
  ordersFeedback?: Record<
    string,
    Record<
      string,
      { feedbackId?: string; liked?: boolean | null; rating?: number | null; feedbackText?: string } | undefined
    >
  >;
  /** Sent as `patientOrgId` on the saved feedback payload. */
  org?: string;
  patientId?: string | null;
  /** Tells the table a save happened so it can refetch on close. */
  setFeedbackSavedFlag?: (saved: boolean) => void;
  /** Writes the mutated feedback map back to the table. */
  setOrdersFeedback?: (ordersFeedback: unknown) => void;
  /** Accepted but unread; `./table.tsx:698` passes it. */
  vendor?: string;
};

/**
 * An order line item as the feedback panel reads it.
 *
 * Wider than the shared {@link FoodOrderItem}: this panel keys its rating maps
 * by `itemId` and copies `vendorId`/`vendorName` onto the saved payload. Kept
 * local because nothing else establishes that the orders feed always sends them.
 *
 * A NON-catalog order has no line item at all — the effect below synthesises one
 * id from `vendorId + '_' + orderName` and writes it back onto the order.
 */
type FeedbackItem = FoodOrderItem & {
  itemId?: string;
  vendorId?: string;
  vendorName?: string;
};

function Feedback(props: FeedbackProps){
    const authInfo = useAuthContext();
    /*
     * The three rating maps, all keyed by an order line item's `itemId` — an
     * open key set off the order data, so every read is `… | undefined`.
     *
     * `like` holds `true`/`false`/`null` (thumbs up, thumbs down, or cleared),
     * `rating` a star count or `null`, `comment` a free-text string.
     */
    const [comment, setComment] = useState<Record<string, string | undefined>>({});
    const [like, setLike] = useState<Record<string, boolean | null | undefined>>({});
    const [rating, setRating] = useState<Record<string, number | null | undefined>>({});
    const [loading, setLoading] = useState(false);
    const [notificationFlag, setNotificationFlag] = useState(false);
    const [notificationMsg, setNotificationMsg] = useState("");
    

    useEffect(()=>{
        /*
         * SUSPECTED DEFECT (this effect) — REPORTED, NOT FIXED.
         * `setLike`/`setRating`/`setComment` are called with these three objects
         * BEFORE the "Load if feedback already exists" block below mutates them
         * in place. React has already taken the reference, so the later writes do
         * land in state — but without a re-render, because the identity never
         * changes. Existing feedback therefore fails to appear until something
         * else re-renders the modal. Moving the setters after the block is the
         * fix and is a behaviour change.
         *
         * `FoodOrderItem` has no `itemId`, so the item keys are read through an
         * index-signature view; `orderItems` is `{quantity, name}` on the shared
         * type while this file also expects `itemId` and `vendorId` on each item.
         */
        const likeObj: Record<string, boolean | null | undefined> = {},
            ratingObj: Record<string, number | null | undefined> = {},
            commentObj: Record<string, string | undefined> = {};
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
        props.order!.orderItems && props.order!.orderItems.map((item: FeedbackItem, _ind) => {
            likeObj[item.itemId!] = null;
            ratingObj[item.itemId!] = null;
            commentObj[item.itemId!] = "";
        })

        // For Non-Catalog Items
        if(!Object.prototype.hasOwnProperty.call(props.order, "orderItems")){
            props.order!.itemId = props.order!.vendorId + "_"+props.order!.orderName!.replaceAll(' ','-')
            props.order!.name = props.order!.orderName!
            likeObj[props.order!.itemId] = null;
            ratingObj[props.order!.itemId] = null;
            commentObj[props.order!.itemId] = "";
        }

        setLike(likeObj);
        setRating(ratingObj);
        setComment(commentObj);
        
        // Load if feedback already exists
        // `orderId!` matches line 165, which reads the same map with the same key.
        const foodFeedback = props.ordersFeedback && props.ordersFeedback[props.order!.orderId!];
        if(foodFeedback){
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
            props.order!.orderItems && props.order!.orderItems.map((item: FeedbackItem, _ind) => {
                likeObj[item.itemId!] = foodFeedback[item.itemId!] && foodFeedback[item.itemId!]!.liked!==null ? foodFeedback[item.itemId!]!.liked : null;
                ratingObj[item.itemId!] = foodFeedback[item.itemId!] && foodFeedback[item.itemId!]!.rating ? foodFeedback[item.itemId!]!.rating : null;
                commentObj[item.itemId!] = foodFeedback[item.itemId!] && foodFeedback[item.itemId!]!.feedbackText ? foodFeedback[item.itemId!]!.feedbackText : "";
            })

            // For Non-Catalog Items
            if(!Object.prototype.hasOwnProperty.call(props.order, "orderItems")){
                const itemId = props.order!.vendorId + "_"+props.order!.orderName!.replaceAll(' ','-');
                
                likeObj[itemId] = foodFeedback[itemId] && foodFeedback[itemId].liked!==null ? foodFeedback[itemId].liked : null;
                ratingObj[itemId] = foodFeedback[itemId] && foodFeedback[itemId].rating ? foodFeedback[itemId].rating : null;
                commentObj[itemId] = foodFeedback[itemId] && foodFeedback[itemId].feedbackText ? foodFeedback[itemId].feedbackText : "";
            }
        }
    },[props.order])


    const saveFeedback = (item: FeedbackItem) =>{
        if(!(like[item.itemId!]===null && rating[item.itemId!]===null && comment[item.itemId!]==="")){
            props.setFeedbackSavedFlag!(true);
            const currentDateTime = moment().toISOString();

        // `Record<string, unknown>` because the four `feedbackId`/`createdBy`/
        // `createdDate` writes below add keys the literal does not declare.
        const payload: Record<string, unknown>={
            "vendorId": item.vendorId,
            "vendorName": item.vendorName,
            "item": item,
            "patientOrgId": props.org,
            "patientId": props.patientId, 
            "lastUpdatedBy":authInfo.practitionerName,
            "lastUpdated":currentDateTime,
            "liked": like[item.itemId!],
            "rating": rating[item.itemId!],
            "feedbackText": comment[item.itemId!],
            "orderId": props.order!.orderId!
        }
        
        if( props.ordersFeedback && props.ordersFeedback[props.order!.orderId!] && props.ordersFeedback[props.order!.orderId!]![item.itemId!]){
            payload["feedbackId"] = props.ordersFeedback[props.order!.orderId!]![item.itemId!]!.feedbackId; 
        }
        else{
            payload["feedbackId"] = uuid();
            payload["createdBy"] = authInfo.practitionerName;
            payload["createdDate"] = currentDateTime;
        }
         setLoading(true);
         setNotificationFlag(false);
        FoodPoster(`/feedback/`+item.itemId, authInfo.token, payload).then((_response) => {
             setLoading(false); 
             setNotificationFlag(true);
             setNotificationMsg("Feedback saved for the item: "+item.name);

             const prevousOrdersFeedback = props.ordersFeedback as Record<string, Record<string, unknown>>;
             if(!Object.prototype.hasOwnProperty.call(props.ordersFeedback, props.order!.orderId!)){         
                prevousOrdersFeedback[props.order!.orderId!] = {}
             }
             prevousOrdersFeedback[props.order!.orderId!]![item.itemId!] = payload;
             props.setOrdersFeedback!(prevousOrdersFeedback);
            
             setTimeout(()=>{
                 setNotificationFlag(false);
             },10000);
        },()=>{
             setLoading(false);
             setNotificationMsg("Error saving the feedback for the item: "+item.name);
             setTimeout(()=>{setNotificationFlag(false);},10000);
        });
        }
    }

    const onChangeRating =(e: React.MouseEvent<HTMLDivElement>, data: RatingProps, item: FeedbackItem) => {
        setRating(prevState => ({
            ...prevState,
            [item.itemId!]: data.rating as number
        }));
    }
    const onChangeLike =(data: boolean | null, item: FeedbackItem) => {
        setLike(prevState => ({
            ...prevState,
            [item.itemId!]: data
        }));
    }
    const onChangeComment =(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData, item: FeedbackItem) => {
        setComment(prevState => ({
            ...prevState,
            [item.itemId!]: data.value
        }));
    }

    const displayFeedbackItems = (item: FeedbackItem, ind: number) => {
        return (<Table.Row key={ind}> 
            <Table.Cell>
            <Icon name="food" color="blue"></Icon>{item.name}
            </Table.Cell>
            <Table.Cell style={{"width":"80px"}}>

            {(like[item.itemId!]===null || like[item.itemId!]===false) && <Icon onClick={()=>onChangeLike(true, item)} name="thumbs up outline" link size='large' />}
            {(like[item.itemId!]!==null && like[item.itemId!]===true) && <Icon name="thumbs up" color="yellow" size='large' />}
            {(like[item.itemId!]===null || like[item.itemId!]===true) && <Icon onClick={()=>onChangeLike(false, item)} name="thumbs down outline" link size='large' />}
            {(like[item.itemId!]!==null && like[item.itemId!]===false) && <Icon name="thumbs down" color="yellow" size='large' />}
            </Table.Cell>
            {/*
              * `rating=` is a standing TS2375: this state holds `null` for an
              * unrated item while Semantic's `RatingProps['rating']` admits only
              * `string | number`. Coercing with `?? undefined` would flip the
              * control from controlled-at-null to uncontrolled on first render.
              */}
            <Table.Cell><Rating maxRating={5} rating={rating[item.itemId!]!} icon='star' size='large' onRate={(e, data)=>onChangeRating(e, data, item)}/></Table.Cell>
            <Table.Cell width={7}>
            
            <Input type="text" value={comment[item.itemId!]} fluid id='comment' onChange={(e, data)=>onChangeComment(e, data, item)} placeholder="comment"/>
            </Table.Cell>
            <Table.Cell>
            <Button
              content='Save'
              link
              
               onClick={()=>saveFeedback(item)}
               
              color='blue'
              size='mini'
              disabled={(like[item.itemId!]===null && rating[item.itemId!]===null && comment[item.itemId!]!.trim()==="")}

            />
            
            </Table.Cell>
        </Table.Row>)
    }

    return (   
        <>
            <Dimmer inverted active={loading}>
                <Loader />
            </Dimmer>
            {notificationMsg!=="" && notificationFlag && <div>
                <Message info content={notificationMsg} />
            </div>}
        <Table celled collapsing compact>
                    <Table.Header>
                    <Table.Row textAlign="center">
                        <Table.HeaderCell>Food Item</Table.HeaderCell>
                        <Table.HeaderCell>Like</Table.HeaderCell>
                        <Table.HeaderCell>Rate</Table.HeaderCell>
                        <Table.HeaderCell>Comment</Table.HeaderCell>
                        <Table.HeaderCell></Table.HeaderCell>
                    </Table.Row>
                    </Table.Header>

                    <Table.Body >
                    {
                    Object.prototype.hasOwnProperty.call(props.order, "orderItems") && props.order!.orderItems!.map((item: FeedbackItem, ind) => 
                    {return displayFeedbackItems(item, ind);
                    })
                    }
                    {
                        !Object.prototype.hasOwnProperty.call(props.order, "orderItems") && displayFeedbackItems(props.order as unknown as FeedbackItem, 1)
                    }

                    </Table.Body>
                </Table>
                {/* {!props.order.hasOwnProperty('orderItems') && <><Icon name='warning sign' size='large' color='yellow'/>At this time, feedback is only allowed for orders created from catalog.</>} */}
                {/* {props.order.orderItems === undefined} */}
        </>     
    )
}

export default Feedback;


